import { useState } from "react";
import { Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useMenu } from "@/hooks/useMenu";
import { useCart } from "@/context/CartContext";
import { StoreHeader } from "@/components/menu/StoreHeader";
import { CategoryPills } from "@/components/menu/CategoryPills";
import { ProductCard } from "@/components/menu/ProductCard";
import { ProductBuilder } from "@/components/menu/ProductBuilder";
import { CartBar } from "@/components/menu/CartBar";
import { CartSheet } from "@/components/menu/CartSheet";
import { PinDialog } from "@/components/menu/PinDialog";

export default function MenuPage() {
  const { data, isLoading, isError } = useMenu();
  const { add } = useCart();
  const [active, setActive] = useState("all");
  const [picked, setPicked] = useState(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [pinOpen, setPinOpen] = useState(false);

  if (isLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>;
  if (isError) return <div className="min-h-screen flex items-center justify-center text-[#A89F91]">No pudimos cargar el menú. Recargá la página.</div>;

  const { settings, categories, products } = data;
  const catById = Object.fromEntries(categories.map((c) => [c.id, c]));
  const shown = active === "all" ? categories : categories.filter((c) => c.id === active);

  const onSelect = (product) => {
    if (catById[product.category_id]?.has_extras) return setPicked(product);
    add({ key: product.id, name: product.name, details: [], unit_price: product.price, qty: 1, image: product.image });
    toast.success(`${product.name} agregado`);
  };

  return (
    <div className="grain min-h-screen pb-32" data-testid="menu-page">
      <StoreHeader settings={settings} />
      <div className="mt-6"><CategoryPills categories={categories} active={active} onChange={setActive} /></div>
      <main className="relative z-10 max-w-3xl mx-auto px-5 mt-6 space-y-10">
        {shown.map((cat) => {
          const list = products.filter((p) => p.category_id === cat.id);
          if (!list.length) return null;
          return (
            <section key={cat.id} data-testid={`category-section-${cat.id}`}>
              <h2 className="text-lg font-bold text-amber-50 mb-4 flex items-center gap-3">
                <span className="w-1.5 h-6 rounded-full bg-[#DC2626]" />{cat.name}
                {cat.half_and_half ? (
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30">Entera o mitad y mitad</span>
                ) : cat.has_extras && cat.extras?.length > 0 ? (
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400 border border-amber-500/30">Con extras</span>
                ) : null}
              </h2>
              <div className="grid gap-3 md:grid-cols-2">
                {list.map((p, i) => <ProductCard key={p.id} product={p} index={i} isPizza={cat.has_extras} onSelect={onSelect} />)}
              </div>
            </section>
          );
        })}
      </main>
      <footer className="relative z-10 max-w-3xl mx-auto px-5 mt-16 py-6 border-t border-[#2E2323] flex items-center justify-between text-xs text-[#6b6157]">
        <span>© {new Date().getFullYear()} {settings.business_name}</span>
        <button onClick={() => setPinOpen(true)} className="p-2 opacity-50 hover:opacity-100 transition-opacity" aria-label="Administración" data-testid="admin-access-btn">
          <Lock className="w-3.5 h-3.5" />
        </button>
      </footer>
      <ProductBuilder product={picked} category={picked && catById[picked.category_id]} siblings={picked ? products.filter((p) => p.category_id === picked.category_id) : []} settings={settings} onClose={() => setPicked(null)} />
      <CartBar onOpen={() => setCartOpen(true)} />
      <CartSheet open={cartOpen} onOpenChange={setCartOpen} />
      <PinDialog open={pinOpen} onOpenChange={setPinOpen} />
    </div>
  );
}
